import type { AgilePlanApproval, AgilePlanRow, AgilePlanRowResult } from './types';

/** The words the Agile Plan card uses, shared by the React card and the /chat template so they cannot drift. */
export const AGILE_PLAN_GROUP_LABEL = 'Approve or decline these Agile Plan changes';
export const AGILE_PLAN_LIST_LABEL = 'Planned changes';
export const AGILE_PLAN_DRY_RUN_LINE = 'Dry run: this is a preview, nothing will be changed';
export const AGILE_PLAN_EXPIRED_LINE = 'This card expired. Ask Emjae again for an updated one.';

const KIND_WORDS: Record<string, string> = {
  create_task: 'New task',
  edit_task: 'Task',
  set_status: 'Task status',
  push_teamwork: 'Teamwork sync',
  edit_notes: 'Notes',
};

export function agilePlanRowLabel(index: number, total: number): string {
  return `Change ${index + 1} of ${total}`;
}

/** "<Task type>, <Billing period>", else the task type, else the kind in words ("Notes"). */
export function agilePlanRowHeading(row: AgilePlanRow): string {
  const type = row.labels?.task_type?.trim();
  const period = row.labels?.billing_period?.trim();
  if (type && period) return `${type}, ${period}`;
  return type || KIND_WORDS[row.kind] || 'Change';
}

const valueText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'empty';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/** "Qty: 2 to 4": the field name with underscores as spaces and a capital first letter; nothing is "empty". */
export function agilePlanChangeLine(change: { field: string; from: unknown; to: unknown }): string {
  const name = change.field.replace(/_/g, ' ');
  return `${name.charAt(0).toUpperCase()}${name.slice(1)}: ${valueText(change.from)} to ${valueText(change.to)}`;
}

export function agilePlanTeamworkLine(row: AgilePlanRow): string | null {
  return row.teamworkIdentityLabel ? `Teamwork: ${row.teamworkIdentityLabel}` : null;
}

/** The expiry line: whole minutes left, rounded up (never "0 minutes"), or the expired sentence once past. */
export function agilePlanExpiry(card: AgilePlanApproval, now: Date): { text: string; expired: boolean } {
  const left = Date.parse(card.expiresAt) - now.getTime();
  if (Number.isNaN(left) || left < 0) return { text: AGILE_PLAN_EXPIRED_LINE, expired: true };
  const minutes = Math.max(1, Math.ceil(left / 60000));
  return { text: `Expires in ${minutes} minute${minutes === 1 ? '' : 's'}`, expired: false };
}

export function agilePlanResultLine(result: AgilePlanRowResult): string {
  if (result.status === 'done') return 'Done';
  const word = result.status === 'skipped' ? 'Skipped' : 'Failed';
  return result.reason ? `${word}: ${result.reason}` : word;
}

export function agilePlanResultFor(results: AgilePlanRowResult[] | undefined, index: number): AgilePlanRowResult | undefined {
  return results?.find((result) => result.index === index);
}
